<script setup lang="ts">
  import { MpContextMenu, MpDivider, MpText } from '@borg/ui';
  import { isNull, isUndefined } from '@borg/utils';

  const props = defineProps<{
    preview?: boolean;
  }>();

  const emits = defineEmits<{
    (eventName: 'edit'): void;
  }>();

  const { d } = useI18n();
  const userStore = useUserStore();

  const user = computed(() => userStore.user);
  const userFullName = computed(() =>
    user.value ? `${user.value.firstName ?? ''} ${user.value.lastName ?? ''}`.trim() : undefined,
  );
  const phoneNumber = computed(() =>
    user.value && user.value.phone
      ? `+${user.value.phone.countryCode} ${user.value.phone.number}`
      : undefined,
  );
  const email = computed(() =>
    user.value
      ? user.value.contactEmail && user.value.contactEmail.length
        ? user.value.contactEmail
        : user.value.email
      : undefined,
  );
  const dateOfBirth = computed(() =>
    user.value && user.value.dateOfBirth
      ? d(...localeDateTimeFormat(new Date(user.value.dateOfBirth), 'dmy'))
      : undefined,
  );

  function showElement(value?: string | null) {
    return !(props.preview && (isNull(value) || isUndefined(value) || value.trim().length === 0));
  }
</script>

<template>
  <div
    v-if="user"
    :class="{
      'container': true,
      'container--preview': preview,
    }"
  >
    <div
      v-if="!preview"
      class="container__avatar"
    >
      <Avatar size="lg" />
    </div>
    <div class="container__information">
      <div class="container__name-container">
        <MpText
          v-if="userFullName"
          as="h2"
          class="container__name"
          no-margin
        >
          {{ userFullName }}
        </MpText>
        <MpText
          v-else
          as="h2"
          shade="30"
          no-margin
          class="container__name container__name--fallback"
        >
          {{ $t('MY_PROFILE_PAGE.PERSONAL_INFO_MODAL.FIRST_NAME_LABEL') }}
          {{ $t('MY_PROFILE_PAGE.PERSONAL_INFO_MODAL.LAST_NAME_LABEL') }}
        </MpText>
        <MpContextMenu
          v-if="!preview"
          class="container__context_menu"
          test="personal-information-context-menu"
          :options="[{ label: $t('EDIT'), onClick: () => emits('edit') }]"
        />
      </div>
      <div class="container__custom-infos">
        <MyProfilePersonalInformationCustomInfo :content="user.title ?? undefined" />
        <MyProfilePersonalInformationCustomInfo
          v-if="!preview"
          :content="user.keywords ?? undefined"
          font-type="body2"
        />
      </div>
      <div class="container__icon-infos">
        <MyProfilePersonalInformationIconInfo
          v-if="showElement(phoneNumber)"
          icon="phone"
          :text="phoneNumber"
          :fallback="$t('MY_PROFILE_PAGE.PHONE_NUMBER_LABEL')"
        />
        <MyProfilePersonalInformationIconInfo
          v-if="showElement(email)"
          icon="mail"
          :text="email"
          :fallback="$t('MY_PROFILE_PAGE.EMAIL_LABEL')"
        />
        <MyProfilePersonalInformationIconInfo
          v-if="showElement(user.address)"
          icon="home"
          :text="user.address ?? undefined"
          :fallback="$t('MY_PROFILE_PAGE.ADDRESS_LABEL')"
        />
      </div>
      <MpDivider v-if="!preview" />
      <div class="container__text-infos">
        <MyProfilePersonalInformationTextInfo
          v-if="showElement(dateOfBirth)"
          :title="$t('MY_PROFILE_PAGE.DOB_LABEL')"
          :content="dateOfBirth"
          :preview="preview"
        />
        <MyProfilePersonalInformationTextInfo
          v-if="showElement(user.citizenship)"
          :title="$t('MY_PROFILE_PAGE.CITIZENSHIP_LABEL')"
          :content="user.citizenship ?? undefined"
          :preview="preview"
        />
        <MyProfilePersonalInformationTextInfo
          v-if="showElement(user.linkedin)"
          :title="$t('MY_PROFILE_PAGE.LINKEDIN_LABEL')"
          :content="user.linkedin ?? undefined"
          :preview="preview"
        />
        <MyProfilePersonalInformationTextInfo
          v-if="showElement(user.webSite)"
          :title="$t('MY_PROFILE_PAGE.WEBSITE_LABEL')"
          :content="user.webSite ?? undefined"
          :preview="preview"
        />
        <MyProfilePersonalInformationTextInfo
          v-if="showElement(user.educationLevel?.name)"
          :title="$t('MY_PROFILE_PAGE.EDUCATION_LABEL')"
          :content="user.educationLevel?.name ?? undefined"
          :preview="preview"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .container {
    $self: &;

    display: flex;
    flex-direction: column;
    gap: var(--mp-space-70);

    &__avatar .avatar {
      outline: none;
    }

    &__information {
      flex: 2;
      word-break: break-word;
    }

    &__name-container {
      display: flex;
      align-items: center;
      margin-bottom: var(--mp-space-30);
    }

    &__name {
      flex: 2;

      &--fallback {
        font-style: italic;
      }
    }

    &__context_menu {
      margin-left: var(--mp-space-70);
    }

    &__custom-infos {
      display: flex;
      flex-direction: column;
      gap: var(--mp-space-30);
      margin-bottom: var(--mp-space-60);
    }

    &__icon-infos {
      display: flex;
      gap: var(--mp-space-40);
      flex-wrap: wrap;
    }

    &__text-infos {
      display: flex;
      flex-direction: column;
      gap: var(--mp-space-40);
    }

    &--preview {
      //background-color: var(--mp-color-grey-100);
      gap: var(--mp-space-60);

      #{$self}__avatar {
        flex: 1;
      }

      #{$self}__information {
        flex: 4;
      }

      #{$self}__icon-infos {
        margin-bottom: var(--mp-space-70);
      }

      #{$self}__text-infos {
        margin-top: var(--mp-space-70);
        flex-direction: row;
        flex-wrap: wrap;
        gap: var(--mp-space-90);
        row-gap: var(--mp-space-40);
      }
    }

    @include screen-md {
      flex-direction: row;
    }
  }
</style>
